/* UI strings.
 *
 * SOURCE: the legacy string tables in EjarMasr.dc.html (en at ~line 494, ar at
 * ~line 528), copied verbatim. The only substitutions are the brand name --
 * "Ejar Masr" / "إيجار مصر" become "SouqAkar" / «سوق عقار» -- and a set of
 * card labels the legacy left hardcoded in English, which are translated here
 * (see docs/w1-corrective-handoff.md, deviation list).
 *
 * LOCALE PURITY: every visible string resolves through this file, so a
 * component cannot inline copy in the wrong script. The brand wordmark in the
 * header renders Latin in both locales; Arabic running copy uses «سوق عقار»,
 * exactly as the legacy did.
 *
 * HONEST SIGNALS: there is no key here for urgency, scarcity or popularity.
 */

export const BRAND_LATIN = "SouqAkar";
export const BRAND_ARABIC = "سوق عقار";

/* The AI advisor persona. It identifies as an AI assistant, never as a
 * person, and makes no pricing guarantees. Quota comes from
 * plans.ai_monthly_limit on the server. */
export const ASSISTANT_LATIN = "SouqAkar Assistant";
export const ASSISTANT_ARABIC = "مساعد سوق عقار";

export type Locale = "ar" | "en";
export type Dir = "rtl" | "ltr";

export type Strings = typeof ar;

const ar = {
  meta: {
    dir: "rtl" as Dir,
    documentTitle: "سوق عقار — بيتك القادم يبدأ من هنا",
    descHome: "ابحث في إعلانات موثّقة للشراء أو الإيجار أو الاستثمار على منصة سوق عقار.",
    descResults: "تصفّح العقارات المتاحة للبيع والإيجار مع فلاتر للمدينة والحي والسعر وعدد الغرف.",
    descPricing: "قارن باقات سوق عقار: حدود الإعلانات، استشارات الذكاء الاصطناعي، وإظهار بيانات التواصل.",
    descPrivacy: "كيف تتعامل منصة سوق عقار مع بياناتك، وما الذي نحتفظ به ولماذا.",
    descSignIn: "سجّل الدخول إلى حسابك على سوق عقار.",
    descRegister: "أنشئ حساباً على سوق عقار كفرد أو كوسيط عقاري.",
    descPanel: "لوحتك: المحفوظات، إعلاناتك، الرسائل، والمستشار الذكي.",
    privacyTitle: "الخصوصية — سوق عقار",
    documentDescription:
      "ابحث في إعلانات موثّقة للشراء أو الإيجار أو الاستثمار على منصة سوق عقار.",
  },
  nav: {
    skipToContent: "تخطَّ إلى المحتوى",
    buy: "شراء",
    rent: "إيجار",
    sell: "بيع",
    agents: "الوسطاء",
    pricing: "الباقات",
    ai: "المساعد الذكي",
    signIn: "تسجيل الدخول",
    subscribe: "اشترك الآن",
    comingSoon: "قريبًا",
  },
  hero: {
    title: "بيتك القادم يبدأ من هنا",
    sub: "ابحث في إعلانات موثّقة للشراء أو الإيجار أو الاستثمار — بدعم الذكاء الاصطناعي.",
    searchLabel: "ابحث عن عقار",
  },
  search: {
    tabBuy: "شراء",
    tabRent: "إيجار",
    searchBtn: "بحث",
    city: "المدينة",
    district: "الحي",
    country: "الدولة",
    anyCity: "كل المدن",
    anyDistrict: "كل الأحياء",
    anyType: "كل الأنواع",
    anyPrice: "كل الأسعار",
    anyBeds: "كل الغرف",
    typeApartment: "شقة",
    typeVilla: "فيلا",
    typeDuplex: "دوبلكس",
    typeStudio: "استوديو",
    typeChalet: "شاليه",
    priceUnder2m: "أقل من ٢ مليون ج.م",
    price2to10m: "٢ – ١٠ مليون ج.م",
    price10mplus: "١٠ مليون+ ج.م",
  },
  /* HOME-1. Eyebrows are Arabic in AR — Bassem's amendment 5: no Latin text
     above an Arabic heading, and «EST. 2026 · CAIRO» is dropped in BOTH
     locales rather than translated. */
  home: {
    eyebrowSelection: "مختارات",
    eyebrowMembership: "الاشتراكات",
    eyebrowAdvisor: "المستشار الذكي",
    browseByArea: "تصفّح حسب المنطقة",
    browseByType: "تصفّح حسب النوع",
    listCta: "اعرض عقارك",
    advertiserTitle: "عندك عقار للبيع أو الإيجار؟",
    advertiserBody: "انشر إعلانك ووصّله للباحثين مباشرةً، وتابع الرسائل من حسابك.",
    /* The design carries this and it is not decoration: the block shows
       what the advisor sounds like, and a reader must not take it for
       advice they were actually given. */
    sampleNotAdvice: "مثال توضيحي — ليست استشارة حقيقية",
    footerPlace: "سوق عقار · القاهرة",
  },
  valueProps: {
    p1Title: "عقارات موثقة",
    p1Body: "يتم التحقق من كل عقار قبل نشره.",
    p2Title: "قرارات بدعم الذكاء الاصطناعي",
    p2Body: "اسأل مستشارنا الذكي لتحليل استثماري فوري.",
    p3Title: "تواصل مباشر مع المعلن",
    p3Body: "راسل المعلنين مباشرةً بحساب مجاني — بدون وسطاء.",
  },
  featured: {
    title: "عقارات مميزة",
    filterAll: "الكل",
    filterSale: "للبيع",
    filterRent: "للإيجار",
    loading: "جارٍ تحميل العقارات…",
    unavailable: "العقارات غير متاحة حاليًا — يرجى المحاولة بعد قليل.",
    empty: "لا توجد عقارات منشورة بعد — تابعنا قريبًا.",
  },
  listing: {
    forSale: "للبيع",
    forRent: "للإيجار",
    beds: "غرف",
    baths: "حمّامات",
    area: "م²",
    furnished: "مفروش",
    viewDetails: "عرض التفاصيل ←",
    noPhoto: "صورة العقار",
    photoAlt: "صورة العقار",
    priceOnRequest: "السعر عند الطلب",
    save: "حفظ العقار",
    mockBadge: "بيانات تجريبية",
    mockBadgeTitle: "هذه البطاقة من بيانات محلية تجريبية، وليست من الخادم.",
  },
  aiTeaser: {
    title: "لست متأكدًا من الشراء أو الإيجار؟",
    body: "تحدث مع مساعد سوق عقار، المدعوم بـ Claude، للحصول على تحليل لأي عقار.",
    btn: "ابدأ استشارة الذكاء الاصطناعي",
  },
  pricingTeaser: { title: "باقات اشتراك بسيطة", btn: "عرض كل الباقات ←" },
  footer: {
    rights: "© 2026 سوق عقار. جميع الحقوق محفوظة.",
    privacy: "سياسة الخصوصية",
  },
  privacy: { title: "سياسة الخصوصية" },
  icons: {
    title: "خيارات الهوية البصرية",
    intro: "ثلاثة خيارات لأيقونة العلامة، معروضة في سياق الاستخدام الفعلي. الاختيار لبسّام.",
    optionA: "الخيار أ — البوّابة",
    optionB: "الخيار ب — علامة الموقع",
    optionC: "الخيار ج — المفتاح والبيت",
    inHeader: "في ترويسة الموقع",
    asFavicon: "كأيقونة تبويب",
    asAppTile: "كأيقونة تطبيق",
    previewTab: "اعرضها كأيقونة تبويب الآن",
  },
  results: {
    title: "نتائج البحث",
    foundLabel: "عقار متاح",
    sortLabel: "ترتيب:",
    sortNewest: "الأحدث",
    sortPriceAsc: "السعر: من الأقل",
    sortPriceDesc: "السعر: من الأعلى",
    filtersLabel: "عوامل التصفية",
    clearFilters: "مسح",
    filterListingType: "نوع الإعلان",
    filterCity: "المدينة / الحي",
    filterPropertyType: "نوع العقار",
    filterPriceRange: "نطاق السعر (جنيه)",
    filterBedrooms: "غرف النوم",
    minPrice: "الحد الأدنى",
    maxPrice: "الحد الأقصى",
    yes: "نعم",
    no: "لا",
    noResults: "لا توجد عقارات مطابقة لعوامل التصفية — حاول توسيع بحثك.",
    unavailable: "العقارات غير متاحة حاليًا — يرجى المحاولة بعد قليل.",
    loading: "جارٍ تحميل النتائج…",
    documentTitle: "نتائج البحث — سوق عقار",
  },
  detail: {
    back: "→ العودة إلى النتائج",
    notAvailable: "هذا العقار غير متاح.",
    loading: "جارٍ تحميل العقار…",
    specsLocation: "الموقع",
    specsArea: "المساحة",
    specsBeds: "غرف النوم",
    specsBaths: "الحمامات",
    specsKitchens: "المطابخ",
    specsFurnished: "مفروش",
    specsVideo: "جولة فيديو",
    yes: "نعم",
    no: "لا",
    gallery: "صور العقار",
    playVideo: "تشغيل الفيديو",
    photoNum: "صورة {n}",
    photoOf: "{n} من {total}",
    videoSection: "فيديو العقار",
    videoPrivacy: "يُحمَّل المشغّل من يوتيوب عند الضغط فقط.",
    mainPhoto: "صورة العقار الرئيسية",
    photo: "صورة",
    videoTour: "جولة فيديو ▶",
    contactTitle: "التواصل مع المعلن",
    contactSignInBody: "سجّل الدخول للتواصل",
    contactSignInBtn: "سجّل الدخول للتواصل",
    contactMemberBody: "أرسل استفساراً إلى المُعلن.",
    contactUnavailable: "تعذّر فتح مراسلة لهذا الإعلان الآن.",
    inquiryLabel: "رسالتك إلى المُعلن",
    inquiryStarter: "مهتم بالعقار ده — ممكن تفاصيل أكتر؟",
    inquiryAbout: "ستُرسل بخصوص: {title}",
    inquirySend: "إرسال",
    inquirySent: "وصلت رسالتك إلى المُعلن ✓",
    inquiryOpenThread: "افتح المحادثة",
    ownContactNote: "هذا إعلانك؛ الاستفسارات تصلك في صندوق الرسائل.",
    contactBtn: "إرسال استفسار",
    mapPrecise: "موقع العقار على الخريطة",
    mapApprox: "المنطقة التقريبية للعقار",
    mapApproxNote: "اختار المعلن عرض المنطقة التقريبية فقط.",
    documentTitleSuffix: "سوق عقار",
  },
  auth: {
    signInTitle: "مرحبًا بعودتك",
    signInSub: "سجّل الدخول لإدارة عقاراتك، العقارات المحفوظة، والمستشار الذكي.",
    identifierLabel: "البريد الإلكتروني أو رقم الموبايل",
    identifierPh: "you@example.com أو 01001234567",
    passwordLabel: "كلمة المرور",
    passwordPh: "••••••••",
    forgotLink: "نسيت كلمة المرور؟",
    signInBtn: "تسجيل الدخول",
    signingIn: "جارٍ تسجيل الدخول…",
    noAccount: "جديد على سوق عقار؟",
    createAccount: "أنشئ حسابًا",
    signOut: "تسجيل الخروج",
    errWrongPassword: "كلمة المرور غير صحيحة. حاول مرة أخرى.",
    errNotFound: "لم يتم العثور على حساب بهذا البريد أو الرقم. تحقق من الإملاء أو أنشئ حسابًا.",
    errNetwork: "تعذّر الاتصال. تحقق من الشبكة وحاول مرة أخرى.",
    errEmpty: "أدخل بريدك الإلكتروني أو رقم موبايلك.",
    sessionExpired: "انتهت جلستك. سجّل الدخول مرة أخرى.",
    resetTitle: "إعادة تعيين كلمة المرور",
    resetSub: "أدخل البريد الإلكتروني أو رقم الموبايل المسجل في حسابك.",
    sendCodeBtn: "إرسال رمز التعيين",
    resetCodeTitle: "أدخل الرمز وكلمة مرور جديدة",
    resetCodeSent: "لقد أرسلنا رمزًا مكوّنًا من 6 أرقام إلى حسابك (إن وجد). أدخله أدناه — قد يستغرق دقيقة، وتحقق من البريد غير المرغوب.",
    codeLabel: "رمز التعيين",
    codePh: "رمز مكون من 6 أرقام",
    newPasswordLabel: "كلمة المرور الجديدة",
    resetBtn: "إعادة تعيين كلمة المرور",
    resetDoneTitle: "تم تحديث كلمة المرور!",
    resetDoneBody: "يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.",
    resetDoneCta: "العودة لتسجيل الدخول",
    errCodeWrong: "الرمز غير مطابق. تحقق من الرمز أعلاه وحاول مرة أخرى.",
    errNewPasswordShort: "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
    errEmailSend: "تعذّر إرسال البريد الآن. حاول مرة أخرى بعد قليل.",
    backToSignIn: "العودة لتسجيل الدخول",
    documentTitleSignIn: "تسجيل الدخول — سوق عقار",
    documentTitleRegister: "إنشاء حساب — سوق عقار",
    /* The password field's own words: the show/hide toggle, the live
     * requirement list and the strength reading. One set, used by every
     * password field on the site — the console's included — so the wording
     * cannot drift between screens. «إظهار / إخفاء كلمة السر» are the ruling's
     * words (AUTH-UX-1, 2026-10-01). */
    pw: {
      show: "إظهار كلمة السر",
      hide: "إخفاء كلمة السر",
      reqLength: "8 أحرف على الأقل",
      reqMix: "حروف وأرقام معًا",
      reqLong: "12 حرفًا أو أكثر",
      required: "مطلوب",
      optional: "اختياري",
      met: "تم",
      unmet: "لم يتحقق بعد",
      strength: "قوة كلمة المرور",
      weak: "ضعيفة",
      fair: "متوسطة",
      strong: "قوية",
      guessable: "سهلة التخمين",
    },
  },
  register: {
    chooseTitle: "كيف ستستخدم سوق عقار؟",
    chooseSub: "هذا يحدد الحقول والتحقق المطلوب منك.",
    individualTitle: "فرد",
    individualBody: "ابحث واحفظ وتواصل مع الوسطاء بخصوص العقارات.",
    agentTitle: "وسيط / مكتب",
    agentBody: "أضف عقارات وأدر نشاطك التجاري على سوق عقار.",
    continueBtn: "متابعة",
    back: "رجوع",
    personalInfo: "بيانات شخصية",
    businessInfo: "بيانات النشاط التجاري",
    fullName: "الاسم الكامل", fullNamePh: "مثال: كريم حسن", fullNameErr: "الاسم الكامل مطلوب",
    mobile: "رقم الموبايل", mobilePh: "مثال: 01001234567", mobileErr: "أدخل رقم موبايل صحيح",
    email: "البريد الإلكتروني", emailPh: "you@example.com", emailErr: "أدخل بريدًا إلكترونيًا صحيحًا",
    password: "كلمة المرور", passwordErr: "يجب أن تكون كلمة المرور 8 أحرف على الأقل",
    businessName: "اسم النشاط التجاري / المكتب", businessNamePh: "مثال: مكتب حسن العقاري", businessNameErr: "اسم النشاط التجاري مطلوب",
    crn: "رقم السجل التجاري", crnPh: "مثال: 123456", crnErr: "رقم السجل التجاري مطلوب",
    vat: "الرقم الضريبي", vatPh: "مثال: 123-456-789", vatErr: "الرقم الضريبي مطلوب",
    submitIndividual: "إنشاء حسابي",
    submitAgent: "إرسال للتحقق",
    doneIndividualTitle: "مرحبًا بك في سوق عقار!",
    doneIndividualBody: "حسابك المجاني جاهز. يمكنك الاشتراك في أي وقت لفتح بيانات التواصل والبريد والمستشار الذكي.",
    doneIndividualCta: "الذهاب إلى حسابي",
    doneAgentTitle: "أنت على وشك الانتهاء!",
    doneAgentBody: "نراجع الآن مستندات السجل التجاري والرقم الضريبي — يستغرق ذلك عادة من يوم إلى يومي عمل. سنرسل لك بريدًا إلكترونيًا فور اعتماد حسابك.",
    doneAgentCta: "العودة للصفحة الرئيسية",
    errExists: "يوجد حساب بهذا البريد أو الرقم بالفعل. سجّل الدخول بدلًا من ذلك.",
    errGeneric: "تعذّر إنشاء الحساب الآن. حاول مرة أخرى بعد قليل.",
    haveAccount: "لديك حساب بالفعل؟",
  },
  panel: {
    myAccount: "حسابي",
    memberFallback: "عضو",
    memberOfPlan: "عضو {plan}",
    ownerPreviewPending: "إعلانك قيد المراجعة — هكذا سيظهر للزوار عند الموافقة.",
    ownerPreviewRejected: "هذا الإعلان مرفوض ولا يظهر للزوار. عدّله ثم أعد إرساله.",
    ownerPreviewPaused: "هذا الإعلان موقوف مؤقتاً ولا يظهر للزوار. أعِد نشره ليظهر من جديد.",
    ownerPreviewGeneric: "هذا الإعلان غير منشور حالياً؛ أنت تراه كصاحبه فقط.",
    rejectionReason: "سبب الرفض",
    pauseListing: "إيقاف مؤقت",
    resumeListing: "إعادة النشر",
    statusChanged: "تم تحديث حالة الإعلان ✓",
    statusFailed: "تعذّر تحديث حالة الإعلان.",
    viewListing: "عرض الإعلان",
    errCountRange: "أدخل رقماً صحيحاً بين {min} و {max}.",
    errWholeNumber: "أدخل رقماً صحيحاً بلا كسور.",
    errPositive: "أدخل رقماً أكبر من صفر.",
    errFixFields: "راجع الحقول المُعلَّمة بالأحمر.",
    changePhoto: "تغيير الصورة",
    noPhotoChosen: "لم تُختر صورة بعد",
    removePhoto: "إزالة الصورة",
    addMorePhotos: "إضافة صور أخرى",
    photoCount: "{n} من {max}",
    photoCapHit: "تم تجاهل {n} صورة: بلغت الحد الأقصى.",
    coverHint: "الصورة الأولى هي صورة الغلاف. اسحب لإعادة الترتيب.",
    cover: "الغلاف",
    moveEarlier: "تحريك للخلف",
    moveLater: "تحريك للأمام",
    videoLabel: "رابط فيديو (اختياري)",
    videoHint: "رابط يوتيوب فقط — مثال: youtube.com/watch?v=… أو youtu.be/…",
    videoInvalid: "هذا ليس رابط يوتيوب صالحاً.",
    videoRemove: "إزالة الفيديو",
    conversation: "محادثة",
    dismiss: "إخفاء",
    dismissTip: "يُخفى من هذا الجهاز فقط في الوقت الحالي؛ الرسالة نفسها لا تُحذف، ويمكنك إظهارها مرة أخرى.",
    dismissed: "رسائل مُخفاة",
    showDismissed: "إظهار المُخفاة",
    hideDismissed: "إخفاؤها مجدداً",
    restoreOne: "إظهار",
    restoreAll: "إظهار الكل",
    noMessagesYet: "لا رسائل في هذه المحادثة بعد.",
    replyPlaceholder: "اكتب رداً…",
    send: "إرسال",
    replyFailed: "تعذّر إرسال الرد. حاول مرة أخرى.",
    navDashboard: "لوحتي",
    welcome: "أهلاً بك",
    welcomeNamed: "أهلاً {name}",
    unreadMessages: "رسائل غير مقروءة",
    aiThisMonth: "استشارات هذا الشهر",
    currentPlan: "باقتك الحالية",
    unreadNote: "عدّاد الرسائل غير المقروءة يحسب الإعلانات العامة الجديدة منذ آخر زيارة لك من هذا الجهاز؛ لا يحتفظ الخادم بحالة قراءة للمحادثات.",
    browse: "تصفّح العقارات",
    navSaved: "المحفوظات", navListings: "إعلاناتي", navMessages: "الرسائل",
    navAi: "المستشار الذكي", navAccount: "الحساب",
    backToSite: "→ العودة إلى الموقع",
    logout: "تسجيل الخروج",
    savedProperties: "العقارات المحفوظة",
    removeFromSaved: "إزالة من المحفوظات",
    noSaved: "لا توجد عقارات محفوظة بعد — تصفّح الإعلانات واضغط القلب للحفظ.",
    myListings: "إعلاناتي", addProperty: "إضافة عقار", edit: "تعديل",
    noMyListings: "لم تقم بإدراج أي عقار بعد.",
    upgradePlan: "ترقية باقتك",
    quotaUsed: "المستخدم", quotaLimit: "الحد", quotaUnlimited: "غير محدود",
    limitReached: "لقد بلغت حد الإعلانات في باقتك الحالية. رقِّ باقتك لإضافة المزيد.",
    backToListings: "→ العودة إلى إعلاناتي",
    title: "العنوان", titlePlaceholder: "مثال: شقة غرفتين بجوار المترو",
    description: "الوصف", descriptionPlaceholder: "اكتب وصفًا واضحًا للعقار: الغرف، التشطيب، الخدمات القريبة…",
    type: "النوع", listingType: "نوع الإعلان",
    location: "الموقع", city: "المدينة", district: "الحي",
    pinHint: "اسحب العلامة لتحديد موقع العقار على الخريطة.",
    precisionLabel: "خصوصية الموقع:",
    exactLoc: "إظهار الموقع الدقيق", approxLoc: "المنطقة التقريبية فقط",
    areaSqm: "المساحة (م²)", priceLabel: "السعر",
    bedrooms: "غرف النوم", bathrooms: "الحمامات", kitchens: "المطابخ",
    furnished: "مفروش", yes: "نعم", no: "لا",
    photo: "صورة العقار", choosePhoto: "اختر صورة", uploading: "جارٍ الرفع…",
    photoRules: "JPEG أو PNG أو WebP، بحد أقصى {mb} ميجابايت.",
    photoLimitsLoading: "جارٍ تحميل حدود الصور…",
    photoLimitsNotSetUp: "حدود الصور غير مُعدّة لهذا السوق ({key}). أبلغ الإدارة.",
    photoLimitsUnavailable: "تعذّر تحميل حدود الصور. حاول مرة أخرى.",
    errPhotoType: "نوع الملف غير مدعوم. اختر JPEG أو PNG أو WebP.",
    errPhotoSize: "الملف أكبر من {mb} ميجابايت.",
    errPhotoUpload: "تعذّر رفع الصورة. حاول مرة أخرى.",
    publish: "نشر الإعلان", publishing: "جارٍ النشر…",
    published: "تم إرسال إعلانك للمراجعة.",
    errRequired: "هذا الحقل مطلوب.",
    errTooLong: "الحد الأقصى {max} حرفًا.",
    errCreate: "تعذّر نشر الإعلان الآن. حاول مرة أخرى.",
    statusLabel: "الحالة",
    messages: "الرسائل", noThreads: "لا توجد رسائل بعد.",
    broadcasts: "إعلانات المنصّة", noBroadcasts: "لا توجد إعلانات.",
    aiTitle: "استشاراتي الذكية", noSessions: "لا توجد استشارات سابقة.",
    aiPending: "المحادثة الجديدة تصل مع فصل المستشار الذكي.",
    aiQuota: "الاستشارات المتاحة هذا الشهر",
    account: "الحساب", name: "الاسم", email: "البريد الإلكتروني", phone: "رقم الموبايل",
    plan: "الباقة", save: "حفظ التغييرات", saving: "جارٍ الحفظ…", saved: "تم الحفظ ✓",
    errSave: "تعذّر الحفظ. حاول مرة أخرى.",
    renew: "تجديد الاشتراك", subscribe: "اشترك الآن",
    emailVerified: "✉✓ البريد مؤكد", emailNotVerified: "✉ البريد غير مؤكد",
    documentTitle: "حسابي — سوق عقار",
  },
  pricing: {
    headline: "اختر باقتك",
    loading: "جارٍ تحميل الباقات…",
    unavailable: "تعذّر تحميل الباقات الآن.",
    perMonth: "/شهريًا",
    free: "مجانًا",
    mostPopular: "الأكثر شيوعًا",
    currentPlan: "باقتك الحالية",
    choosePlan: "اختر الباقة",
    featListings: "إعلانات",
    featListingsUnlimited: "إعلانات بلا حد",
    featAi: "استشارة ذكية شهريًا",
    featAiUnlimited: "استشارات ذكية بلا حد",
    featAiNone: "بدون استشارات ذكية",
    featContact: "التواصل مع المعلنين",
    documentTitle: "الباقات — سوق عقار",
    checkoutTitle: "إتمام الاشتراك",
    planLabel: "الباقة",
    priceLabel: "المبلغ",
    promoCodeLabel: "كود الخصم (اختياري)",
    promoCodePh: "أدخل الكود إن وُجد",
    confirmBtn: "تأكيد الاشتراك",
    working: "جارٍ التنفيذ…",
    unlimited: "بلا حد",
    processing: "جارٍ التنفيذ…",
    successTitle: "تم تفعيل باقتك",
    renewsAt: "يتجدد في",
    reference: "الرقم المرجعي",
    backToAccount: "→ الذهاب إلى حسابي",
    errCheckout: "تعذّر إتمام الاشتراك. حاول مرة أخرى.",
    signInFirst: "سجّل الدخول لإتمام الاشتراك.",
    stepReview: "المراجعة",
    stepPayment: "الدفع",
    stepDone: "تم",
    currentPlanIs: "باقتك الحالية",
    alreadyOnPlan: "أنت مشترك بالفعل في هذه الباقة.",
    renewsOn: "يتجدد في",
    downgradeTitle: "أنت على وشك النزول إلى باقة أقل",
    downgradeFrom: "لديك الآن",
    downgradeTo: "ستنتقل إلى",
    downgradeLose: "ما ستفقده:",
    lossAi: "استشارات الذكاء الاصطناعي شهرياً",
    lossListings: "عدد الإعلانات",
    lossContact: "إظهار بيانات التواصل",
    lossShown: "ظاهرة",
    lossHidden: "مخفية",
    downgradeConfirm: "أفهم ذلك، تابع",
    downgradeCancel: "إبقِ باقتي الحالية",
    payTitle: "طريقة الدفع",
    payCard: "بطاقة",
    payFawry: "فوري",
    payVodafone: "فودافون كاش",
    cardNumber: "رقم البطاقة",
    cardName: "الاسم على البطاقة",
    cardExpiry: "تاريخ الانتهاء",
    cardCvc: "الرمز السري",
    cardInvalid: "بيانات البطاقة غير صحيحة.",
    cardDeclined: "رُفضت البطاقة. في وضع التجربة تُقبل بطاقات الاختبار فقط.",
    vodafoneNumber: "رقم فودافون كاش",
    vodafonePh: "01xxxxxxxxx",
    vodafoneInvalid: "رقم غير صحيح.",
    fawryNote: "ادفع هذا الكود في أي فرع فوري خلال ٢٤ ساعة.",
    testMode: "وضع تجريبي — لن يتم خصم أي مبلغ",
    testCardsHint: "بطاقات الاختبار: 4242 4242 4242 4242 أو 5555 5555 5555 4444",
    payNow: "تأكيد الدفع",
    continueToPay: "المتابعة إلى الدفع",
    zeroPriceSkip: "هذه الباقة مجانية لك الآن، فلا خطوة دفع.",
    mockNotice: "لم تتم أي عملية دفع فعلية — الاشتراك مفعّل مجانًا حاليًا.",
  },
  chat: {
    title: "مساعد سوق عقار",
    subtitle: "مساعد آلي — الإجابات تقديرية وليست ضمانًا.",
    greeting: "مرحبًا! أنا مساعد سوق عقار، مساعد آلي. اسألني عن الشراء مقابل الإيجار، أو الأسعار، أو أرسل بيانات عقار لتحليل سريع. كل الأرقام تقديرية.",
    placeholder: "اكتب سؤالك…",
    send: "إرسال",
    thinking: "جارٍ التفكير…",
    quotaLeft: "المتبقي هذا الشهر",
    quotaSpent: "لقد استهلكت استشارات هذا الشهر. رقِّ باقتك للمزيد.",
    signInFirst: "سجّل الدخول لاستخدام المساعد.",
    errNetwork: "تعذّر الاتصال بالمساعد الآن.",
    errBlocked: "المساعد غير متاح من هذا النطاق حاليًا — يجري تحديث الخادم.",
    errGeneric: "حدث خطأ. حاول مرة أخرى.",
    endAndSave: "إنهاء وحفظ المحادثة",
    saved: "تم حفظ المحادثة ✓",
    saveFailed: "تعذّر حفظ المحادثة.",
  },
  /* ── Admin console ──────────────────────────────────────────────────────
   * The legacy console was English-only. It is bilingual here because the
   * locale-purity rule has no exemption for internal screens. */
  admin: {
    consoleTitle: "لوحة التحكم",
    signIn: {
      title: "دخول المشرفين",
      subtitle: "هذه اللوحة للمشرفين فقط. الجلسة تنتهي بعد ٢٤ ساعة.",
      identifier: "البريد الإلكتروني أو رقم الهاتف",
      password: "كلمة المرور",
      submit: "دخول",
      badCreds: "بيانات الدخول غير صحيحة.",
      failed: "تعذّر تسجيل الدخول. حاول مرة أخرى.",
      rateLimited: "محاولات كثيرة. انتظر قليلاً ثم أعد المحاولة.",
      memberHint: "هل تبحث عن حسابك الشخصي؟ سجّل الدخول من هنا.",
    },
    nav: {
      dashboard: "لوحة المعلومات",
      users: "المستخدمون",
      listings: "الإعلانات",
      verification: "توثيق الوسطاء",
      subscriptions: "الاشتراكات",
      broadcast: "الرسائل العامة",
      markets: "الأسواق",
      reports: "التقارير",
      payments: "المدفوعات",
      settings: "الإعدادات",
      admins: "المشرفون",
      signOut: "تسجيل الخروج",
      backToSite: "العودة إلى الموقع",
    },
    common: {
      search: "بحث",
      refresh: "تحديث",
      save: "حفظ",
      saving: "جارٍ الحفظ…",
      saved: "تم الحفظ ✓",
      cancel: "إلغاء",
      edit: "تعديل",
      loading: "جارٍ التحميل…",
      empty: "لا توجد بيانات.",
      retry: "إعادة المحاولة",
      all: "الكل",
      unlimited: "بلا حد",
      none: "—",
      yes: "نعم",
      no: "لا",
      actions: "إجراءات",
      count: "العدد",
      required: "هذا الحقل مطلوب.",
    },
    errors: {
      sessionExpired: "انتهت جلسة المشرف. سجّل الدخول مرة أخرى.",
      forbidden: "صلاحيتك لا تسمح بهذا الإجراء.",
      needsRole: "هذه الشاشة متاحة لصلاحية «{role}».",
      loadFailed: "تعذّر تحميل البيانات.",
      saveFailed: "تعذّر الحفظ.",
    },
    health: {
      title: "صحة النظام",
      note: "كل الفحوص هنا للقراءة فقط: لا تكتب شيئاً ولا تستهلك أي رصيد.",
      run: "إعادة الفحص",
      running: "جارٍ الفحص…",
      lastRun: "آخر فحص",
      build: "نسخة البناء",
      buildUnknown: "غير معروفة",
      branch: "الفرع",
      buildNote: "تُقرأ من الـ commit وقت البناء، فلا يمكن أن تخالف ما تم بناؤه فعلاً.",
      probe: "الفحص",
      verdict: "النتيجة",
      detail: "التفاصيل",
      layer: "الطبقة",
      ms: "الزمن",
      ok: "سليم",
      warn: "تحذير",
      fail: "فشل",
      skipped: "متخطّى",
      layerNetwork: "الشبكة",
      layerCors: "سياسة المصادر",
      layerProxy: "الوسيط",
      layerUpstream: "الخدمة الخلفية",
      layerAuth: "الجلسة",
      layerClient: "المتصفح",
      pProxy: "وسيط قاعدة البيانات",
      pProxyWhat: "‏GET /countries?limit=1 — أرخص قراءة ممكنة.",
      pPlans: "الباقات العامة",
      pPlansWhat: "‏GET /plans — ووجود عمود إظهار بيانات التواصل.",
      pAuth: "جلسة المشرف",
      pAuthWhat: "قراءة إدارية واحدة للتأكد أن الرمز ما زال صالحاً.",
      pClaude: "وسيط المساعد الذكي",
      pClaudeWhat: "طلب GET بسيط — لا تُرسل أي رسالة ولا يُستهلك رصيد.",
      pStorage: "تخزين الصور",
      pStorageWhat: "‏HEAD عبر الوسيط — لا يُرفع أي ملف.",
      storagePending:
        "فحص «تخزين الصور» متوقّف مؤقتاً: كان يقيس مشروعاً قديماً متوقّفاً ويُظهره سليماً."
        + " سيعود بعد نشر إصدار الوسيط v3.8.11.",
      liveCall: "اختبار مكالمة حقيقية",
      liveCallWarn: "يستهلك استشارة واحدة من رصيدك الشهري.",
      liveCallConfirm: "إرسال رسالة اختبار حقيقية؟ ستُخصم استشارة واحدة.",
      allOk: "كل الفحوص سليمة.",
      someFail: "هناك فحص فاشل — التفاصيل بالأسفل تحدّد الطبقة المسؤولة.",
      corsHint: "رفض المصدر يعني تغييراً في قائمة السماح داخل الـ worker، لا انقطاعاً في الخدمة.",
    },
    dashboard: {
      title: "لوحة المعلومات",
      users: "المستخدمون",
      agents: "الوسطاء",
      individuals: "الأفراد",
      activeListings: "إعلانات منشورة",
      pendingListings: "إعلانات بانتظار المراجعة",
      rejectedListings: "إعلانات مرفوضة",
      pendingVerifications: "طلبات توثيق معلّقة",
      paidSubscribers: "مشتركون مدفوعون",
      countedNote: "كل رقم هنا عدّ فعلي لصفوف قاعدة البيانات وقت التحميل، وليس تقديراً.",
      noRevenue: "لا يوجد رقم إيرادات هنا: المنصة لا تسجّل المدفوعات في قاعدة البيانات بعد، فأي مبلغ معروض سيكون مُختلقاً.",
    },
    users: {
      title: "المستخدمون",
      name: "الاسم",
      email: "البريد",
      phone: "الهاتف",
      role: "النوع",
      plan: "الباقة",
      status: "الحالة",
      subscription: "الاشتراك",
      joined: "تاريخ الانضمام",
      renews: "التجديد",
      verified: "موثّق",
      emailVerified: "بريد مُفعّل",
      searchPlaceholder: "ابحث بالاسم أو البريد أو الهاتف",
      editTitle: "تعديل المستخدم",
      noDelete: "لا يمكن حذف المستخدمين من هذه اللوحة.",
    },
    listings: {
      title: "مراجعة الإعلانات",
      listing: "الإعلان",
      owner: "المالك",
      price: "السعر",
      location: "الموقع",
      status: "الحالة",
      tag: "الوسم",
      views: "مشاهدات",
      inquiries: "استفسارات",
      approve: "اعتماد",
      /* Shown beside a disabled «اعتماد»: the rule and the number, so nobody
         is left looking at a grey button. */
      approveNeedsPhotos: "لا يمكن الاعتماد: هذا الإعلان يحتاج {n} صورة على الأقل.",
      reject: "رفض",
      pause: "إيقاف مؤقت",
      reactivate: "إعادة النشر",
      flagReason: "سبب الرفض",
      flagReasonPlaceholder: "اكتب سبباً يظهر لصاحب الإعلان",
      flagReasonRequired: "سبب الرفض مطلوب قبل الرفض.",
      returnToReview: "إعادة للمراجعة",
      moderated: "تم تحديث حالة الإعلان ✓",
      titlePolicy: "يظهر العنوان كما كتبه صاحب الإعلان تماماً.",
    },
    verification: {
      title: "طلبات توثيق الوسطاء",
      applicant: "مقدّم الطلب",
      business: "الاسم التجاري",
      crn: "السجل التجاري",
      vat: "البطاقة الضريبية",
      experience: "سنوات الخبرة",
      documents: "المستندات",
      nationalId: "بطاقة الرقم القومي",
      license: "الترخيص",
      selfie: "صورة شخصية",
      officeContacts: "بيانات المكتب",
      approve: "اعتماد",
      reject: "رفض",
      rejectReason: "سبب الرفض",
      rejectReasonRequired: "سبب الرفض مطلوب قبل الرفض.",
      updated: "تم تحديث الطلب ✓",
      docsNote: "اللوحة تعرض ما إذا كان المستند مرفوعاً فقط؛ الملفات نفسها ليست متاحة عبر الواجهة الحالية.",
    },
    plans: {
      title: "الباقات",
      tabPlans: "الباقات",
      tabPromos: "العروض",
      newPlan: "+ باقة جديدة",
      newPromo: "+ عرض جديد",
      subscribers: "المشتركون",
      statusActive: "مفعّلة",
      statusArchived: "مؤرشفة",
      archive: "أرشفة",
      restore: "استرجاع",
      editPlan: "تعديل الباقة",
      createPlan: "باقة جديدة",
      archiveConfirm: "{n} مشترك على هذه الباقة؛ الأرشفة تخفيها عن صفحة الأسعار ولا تمس المشتركين الحاليين.",
      archiveConfirmZero: "لا مشترك على هذه الباقة؛ الأرشفة تخفيها عن صفحة الأسعار.",
      restoreConfirm: "إعادة إظهار هذه الباقة في صفحة الأسعار؟",
      newPlanEnabledNote: "تصل الباقة الجديدة مفعّلة من الخادم، فتظهر في صفحة الأسعار فور إنشائها.",
      noDeleteLaw: "لا حذف للباقات: الأرشفة فقط، حفاظاً على سجلّ المشتركين.",
      promoLive: "نشط",
      promoScheduled: "مجدول",
      promoExpired: "منتهي",
      promoPaused: "موقوف",
      promoWindow: "المدة",
      promoUses: "الاستخدام",
      promoTarget: "الباقة",
      promoDiscount: "الخصم",
      launchCampaign: "حملة الإطلاق",
      launchGuard: "هذا عرض الإطلاق الجاري. متأكد من تعديله؟",
      pause: "إيقاف",
      activate: "تفعيل",
      editPromo: "تعديل العرض",
      createPromo: "عرض جديد",
      deleteBlocked: "لا يمكن حذف عرض استُخدم بالفعل؛ أوقفه بدلاً من ذلك.",
      endsIn: "ينتهي {when}",
      extend7: "+٧ أيام",
      allPaidPlans: "كل الباقات المدفوعة",
      plansCount: "{n} باقات",
      expandRows: "عرض الصفوف",
      collapseRows: "إخفاء الصفوف",
      mixedState: "مختلط",
      codedMultiBlocked: "لا يمكن استخدام كود واحد لأكثر من باقة: قيد قاعدة البيانات يفرض تفرّد الكود. اجعل الحملة تلقائية (بلا كود)، أو أنشئ عرضاً لكل باقة بكود مختلف.",
      perPlanResult: "نتيجة كل باقة",
      rowFailed: "فشل",
      rowOk: "تم",
      retryFailed: "إعادة محاولة الفاشل",
      creatingRows: "جارٍ الإنشاء: {done} من {total}",
      extend30: "+٣٠ يوم",
      extendTitle: "تمديد نهاية العرض",
      extendedTo: "امتد حتى {date}",
      usedEditWarn: "استُخدم هذا العرض {n} مرة. من استفاد منه يحتفظ بشروطه؛ التعديل يسري على عمليات الشراء القادمة فقط. متابعة؟",
      usedNoDelete: "لا يظهر الحذف لعرض استُخدم: سجلّات الاستفادة تشير إليه. أوقفه بدلاً من ذلك.",
      name: "الاسم",
      price: "السعر",
      maxProperties: "حد الإعلانات",
      listingDuration: "مدة الإعلان (يوم)",
      listingDurationHelp: "فارغ في الباقة المجانية: تأخذ مدتها من إعداد السوق «مدة الإعلان المجاني».",
      refreshesPerMonth: "تحديثات في الشهر",
      refreshesHelp: "٠ = لا تحديثات، وفارغ = بلا حد.",
      aiLimit: "حد استشارات الذكاء الاصطناعي",
      enabled: "مفعّلة",
      contactEnabled: "إظهار بيانات التواصل",
      contactHelp: "عند الإيقاف تُخفى بيانات تواصل المُعلن عن أعضاء هذه الباقة.",
      unlimitedHelp: "اترك الحقل فارغاً ليعني «بلا حد».",
    },
    promotions: {
      title: "العروض",
      name: "اسم العرض",
      plan: "الباقة",
      discountType: "نوع الخصم",
      percent: "نسبة مئوية",
      fixed: "مبلغ ثابت",
      discountValue: "قيمة الخصم",
      startsAt: "يبدأ",
      endsAt: "ينتهي",
      active: "مفعّل",
      durationDays: "مدة الاشتراك (بالأيام)",
      message: "رسالة تصل للمشترك",
      code: "الكود",
      codeHelp: "اتركه فارغاً ليكون العرض تلقائياً لكل الزوار.",
      maxUses: "أقصى عدد استخدامات",
      usesCount: "مرات الاستخدام",
      oncePerUser: "مرة واحدة لكل مستخدم",
      create: "إنشاء عرض",
      remove: "حذف",
      confirmRemove: "حذف هذا العرض نهائياً؟",
      created: "تم إنشاء العرض ✓",
      removed: "تم حذف العرض ✓",
      automatic: "تلقائي",
    },
    broadcast: {
      title: "الرسائل العامة",
      subject: "الموضوع",
      message: "نص الرسالة",
      target: "المستقبِل",
      everyone: "كل الأعضاء",
      oneUser: "عضو محدّد",
      pickUser: "اختر العضو",
      send: "إرسال",
      sent: "تم الإرسال ✓",
      history: "الرسائل السابقة",
      recipients: "المستقبلون",
      date: "التاريخ",
      toAll: "للجميع",
      sendNote: "تصل الرسالة إلى صندوق الوارد داخل المنصة فقط؛ لا يُرسل بريد إلكتروني.",
    },
    admins: {
      title: "المشرفون",
      superOnly: "إدارة حسابات المشرفين متاحة للمشرف الأعلى فقط.",
      name: "الاسم",
      email: "البريد",
      phone: "الهاتف",
      role: "الصلاحية",
      status: "الحالة",
      newAdmin: "مشرف جديد",
      password: "كلمة المرور",
      passwordHelp: "8 أحرف على الأقل.",
      roleAdmin: "مشرف",
      roleSupervisor: "مراقب",
      roleSuper: "مشرف أعلى",
      noSuperCreate: "لا يمكن إنشاء «مشرف أعلى» من هذه اللوحة.",
      remove: "حذف",
      confirmRemove: "حذف حساب هذا المشرف؟",
      created: "تم إنشاء الحساب ✓",
      removed: "تم حذف الحساب ✓",
    },
    markets: {
      title: "الأسواق",
      countries: "الدول",
      cities: "المدن",
      districts: "الأحياء",
      nameAr: "الاسم بالعربية",
      nameEn: "الاسم بالإنجليزية",
      isoCode: "رمز الدولة",
      currency: "العملة",
      phoneCode: "مفتاح الهاتف",
      activeState: "الحالة",
      active: "مفعّل",
      inactive: "موقوف",
      readOnly: "الدول والمدن للعرض فقط: الخادم لا يوفّر مساراً لتعديلها بعد. الاسم المختصر للحي هو الحقل الوحيد القابل للتعديل هنا.",
      inCity: "في",
      shortName: "الاسم المختصر",
      shortNameAr: "بالعربية",
      shortNameEn: "English",
      shortNameHelp: "يظهر على بطاقة المنطقة في الرئيسية. اتركه فارغاً لإظهار الاسم الكامل.",
      shortNameTooLong: "الحد الأقصى ٤٠ حرفاً.",
    },
    reports: {
      title: "التقارير",
      listingsByStatus: "الإعلانات حسب الحالة",
      listingsByCity: "الإعلانات حسب المدينة",
      listingsByType: "الإعلانات حسب النوع",
      usersByPlan: "المستخدمون حسب الباقة",
      promoUsage: "استخدام العروض",
      derivedNote: "كل ما في هذه الصفحة محسوب من صفوف حقيقية وقت التحميل.",
      noRevenue: "لا يوجد تقرير إيرادات: لا تحتفظ قاعدة البيانات بجدول للمدفوعات، ولا يمكن اشتقاق مبلغ صحيح من البيانات المتاحة.",
    },
    payments: {
      title: "المدفوعات",
      noSource: "لا تسجّل المنصة أي عملية دفع في قاعدة البيانات، فلا توجد بيانات لعرضها هنا.",
      legacyNote: "كانت اللوحة القديمة تعرض ست عمليات دفع بأسماء ومبالغ وتواريخ ثابتة مكتوبة داخل الصفحة، وليست بيانات حقيقية. حُذفت بدلاً من إعادة إنتاجها.",
      needed: "يحتاج تفعيل هذه الشاشة إلى جدول مدفوعات ومسار قراءة له.",
    },
    settings: {
      title: "الإعدادات",
      account: "حسابك",
      signedInAs: "الحساب الحالي",
      role: "الصلاحية",
      changePassword: "تغيير كلمة المرور",
      newPassword: "كلمة المرور الجديدة",
      tooShort: "كلمة المرور 8 أحرف على الأقل.",
      changed: "تم تغيير كلمة المرور ✓",
      platformToggles: "إعدادات المنصة",
      togglesNote: "لا توجد إعدادات منصة قابلة للتغيير هنا. كانت مفاتيح اللوحة القديمة (التسجيل، الذكاء الاصطناعي، وضع الصيانة) تُحفظ في متصفح المشرف وحده ولا تؤثر على المنصة إطلاقاً؛ لم تُعَد كتابتها لأن مفتاحاً لا يفعل شيئاً أسوأ من غيابه.",
      reloadNote: "بعد إعادة تحميل الصفحة تبقى الجلسة صالحة لكن اسم الحساب لا يظهر حتى تسجّل الدخول من جديد.",
    },
  },
  notFound: {
    title: "الصفحة غير موجودة",
    body: "الرابط الذي فتحته لا يؤدي إلى صفحة على سوق عقار. قد يكون قديماً أو به خطأ إملائي.",
    home: "الصفحة الرئيسية",
    search: "تصفّح العقارات",
    pricing: "الباقات والأسعار",
  },
  agent: {
    documentTitle: "لوحة الوسيط — سوق عقار",
    title: "لوحة الوسيط",
    navDashboard: "الأداء",
    navListings: "عقاراتي",
    navMessages: "الرسائل",
    navAi: "المستشار الذكي",
    navAccount: "الحساب",
    tierGold: "وسيط ذهبي",
    tierPlatinum: "وسيط بلاتيني",
    verified: "وسيط موثّق",
    notVerified: "لم يكتمل التوثيق بعد",
    verificationPending: "طلب التوثيق قيد المراجعة.",
    verificationRejected: "طلب التوثيق مرفوض.",
    totalListings: "إجمالي العقارات",
    activeListings: "عقارات منشورة",
    totalViews: "إجمالي المشاهدات",
    totalInquiries: "إجمالي الاستفسارات",
    perListing: "الأداء لكل عقار",
    listing: "العقار",
    views: "مشاهدات",
    inquiries: "استفسارات",
    noListings: "لم تُضف أي عقار بعد.",
    countedNote: "الأرقام مأخوذة من عدّادات المشاهدات والاستفسارات المسجّلة على كل عقار.",
    noLeadRecords: "لا يوجد سجل تفصيلي بالعملاء المحتملين: المنصة تحصي الاستفسارات ولا تحتفظ ببيانات كل استفسار على حدة.",
    needPlan: "لوحة الوسيط متاحة لباقات الوسطاء المدفوعة.",
    seePlans: "عرض الباقات",
  },
  status: { fixtureNotice: "يتم العرض ببيانات تجريبية محلية." },
};

const en: Strings = {
  meta: {
    dir: "ltr" as Dir,
    documentTitle: "SouqAkar — Find your next home",
    descHome: "Search verified listings to buy, rent or invest on SouqAkar.",
    descResults: "Browse properties for sale and rent, with filters for city, district, price and rooms.",
    descPricing: "Compare SouqAkar plans: listing allowances, AI consultations and contact visibility.",
    descPrivacy: "How SouqAkar handles your data, what we keep, and why.",
    descSignIn: "Sign in to your SouqAkar account.",
    descRegister: "Create a SouqAkar account as an individual or a property agent.",
    descPanel: "Your panel: saved properties, your listings, messages and the AI advisor.",
    privacyTitle: "Privacy — SouqAkar",
    documentDescription:
      "Search verified listings to buy, rent or invest on SouqAkar.",
  },
  nav: {
    skipToContent: "Skip to content",
    buy: "Buy",
    rent: "Rent",
    sell: "Sell",
    agents: "Agents",
    pricing: "Pricing",
    ai: "AI Assistant",
    signIn: "Sign in",
    subscribe: "Subscribe",
    comingSoon: "Coming soon",
  },
  hero: {
    title: "Find your next home",
    sub: "Search verified listings to buy, rent or invest — powered by AI insights.",
    searchLabel: "Search for a property",
  },
  search: {
    tabBuy: "Buy",
    tabRent: "Rent",
    searchBtn: "Search",
    city: "City",
    district: "District",
    country: "Country",
    anyCity: "All cities",
    anyDistrict: "All districts",
    anyType: "Any type",
    anyPrice: "Any price",
    anyBeds: "Any beds",
    typeApartment: "Apartment",
    typeVilla: "Villa",
    typeDuplex: "Duplex",
    typeStudio: "Studio",
    typeChalet: "Chalet",
    priceUnder2m: "Under 2M EGP",
    price2to10m: "2M – 10M EGP",
    price10mplus: "10M+ EGP",
  },
  home: {
    eyebrowSelection: "SELECTION",
    eyebrowMembership: "MEMBERSHIP",
    eyebrowAdvisor: "AI ADVISOR",
    browseByArea: "Browse by area",
    browseByType: "Browse by type",
    listCta: "List your property",
    advertiserTitle: "Have a property to sell or rent?",
    advertiserBody: "Publish your listing, reach searchers directly, and handle every message from your account.",
    sampleNotAdvice: "Illustrative example — not real advice",
    footerPlace: "SOUQAKAR · CAIRO",
  },
  valueProps: {
    p1Title: "Verified listings",
    p1Body: "Every property is checked before it goes live.",
    p2Title: "AI-powered decisions",
    p2Body: "Ask our AI advisor for an instant investment read.",
    p3Title: "Contact listers directly",
    p3Body: "Message agents directly with a free account — no middlemen.",
  },
  featured: {
    title: "Featured properties",
    filterAll: "All",
    filterSale: "For Sale",
    filterRent: "For Rent",
    loading: "Loading listings…",
    unavailable: "Listings are unavailable right now — please try again shortly.",
    empty: "No listings are live yet — check back soon.",
  },
  listing: {
    forSale: "For Sale",
    forRent: "For Rent",
    beds: "beds",
    baths: "baths",
    area: "m²",
    furnished: "Furnished",
    viewDetails: "View details →",
    noPhoto: "property photo",
    photoAlt: "Property photo",
    priceOnRequest: "Price on request",
    save: "Save property",
    mockBadge: "Sample data",
    mockBadgeTitle: "This card comes from local sample data, not the server.",
  },
  aiTeaser: {
    title: "Not sure whether to buy or rent?",
    body: "Chat with SouqAkar Assistant — powered by Claude — for a personalized read on any property.",
    btn: "Start AI consultation",
  },
  pricingTeaser: { title: "Simple membership plans", btn: "See all plans →" },
  footer: {
    rights: "© 2026 SouqAkar. All rights reserved.",
    privacy: "Privacy policy",
  },
  privacy: { title: "Privacy policy" },
  icons: {
    title: "Brand mark options",
    intro: "Three options for the brand mark, shown in real usage context. The choice is Bassem's.",
    optionA: "Option A — The archway",
    optionB: "Option B — The location pin",
    optionC: "Option C — The key and home",
    inHeader: "In the site header",
    asFavicon: "As a tab icon",
    asAppTile: "As an app icon",
    previewTab: "Preview it as this tab’s icon",
  },
  results: {
    title: "Search results",
    foundLabel: "properties found",
    sortLabel: "Sort:",
    sortNewest: "Newest",
    sortPriceAsc: "Price: low to high",
    sortPriceDesc: "Price: high to low",
    filtersLabel: "Filters",
    clearFilters: "Clear",
    filterListingType: "Listing type",
    filterCity: "City / district",
    filterPropertyType: "Property type",
    filterPriceRange: "Price range (EGP)",
    filterBedrooms: "Bedrooms",
    minPrice: "Min",
    maxPrice: "Max",
    yes: "Yes",
    no: "No",
    noResults: "No properties match your filters — try widening your search.",
    unavailable: "Listings are unavailable right now — please try again shortly.",
    loading: "Loading results…",
    documentTitle: "Search results — SouqAkar",
  },
  detail: {
    back: "← Back to results",
    notAvailable: "This listing isn't available.",
    loading: "Loading listing…",
    specsLocation: "Location",
    specsArea: "Area",
    specsBeds: "Bedrooms",
    specsBaths: "Bathrooms",
    specsKitchens: "Kitchens",
    specsFurnished: "Furnished",
    specsVideo: "Video tour",
    yes: "Yes",
    no: "No",
    gallery: "Property photos",
    playVideo: "Play video",
    photoNum: "Photo {n}",
    photoOf: "{n} of {total}",
    videoSection: "Property video",
    videoPrivacy: "The player loads from YouTube only when you press play.",
    mainPhoto: "main property photo",
    photo: "photo",
    videoTour: "video tour ▶",
    contactTitle: "Agent & contact",
    contactSignInBody: "Sign in to contact",
    contactSignInBtn: "Sign in to contact",
    contactMemberBody: "Send an inquiry to the lister.",
    contactUnavailable: "Messaging is not available for this listing right now.",
    inquiryLabel: "Your message to the lister",
    inquiryStarter: "I am interested in this property — could I have more details?",
    inquiryAbout: "Sent about: {title}",
    inquirySend: "Send",
    inquirySent: "Your message reached the lister ✓",
    inquiryOpenThread: "Open the conversation",
    ownContactNote: "This is your listing; inquiries arrive in your Messages.",
    contactBtn: "Send inquiry",
    mapPrecise: "Property location on the map",
    mapApprox: "Approximate area of the property",
    mapApproxNote: "The lister chose to show the approximate area only.",
    documentTitleSuffix: "SouqAkar",
  },
  auth: {
    signInTitle: "Welcome back",
    signInSub: "Sign in to manage your listings, saved properties and the AI assistant.",
    identifierLabel: "Email or mobile number",
    identifierPh: "you@example.com or 01001234567",
    passwordLabel: "Password",
    passwordPh: "••••••••",
    forgotLink: "Forgot your password?",
    signInBtn: "Sign in",
    signingIn: "Signing in…",
    noAccount: "New to SouqAkar?",
    createAccount: "Create an account",
    signOut: "Sign out",
    errWrongPassword: "That password is incorrect. Try again.",
    errNotFound: "No account found for that email or number. Check the spelling or create an account.",
    errNetwork: "Could not connect. Check your network and try again.",
    errEmpty: "Enter your email or mobile number.",
    sessionExpired: "Your session ended. Please sign in again.",
    resetTitle: "Reset your password",
    resetSub: "Enter the email or mobile number on your account.",
    sendCodeBtn: "Send reset code",
    resetCodeTitle: "Enter the code and a new password",
    resetCodeSent: "We sent a 6-digit code to your account, if one exists. Enter it below — it can take a minute, and check your spam folder.",
    codeLabel: "Reset code",
    codePh: "6-digit code",
    newPasswordLabel: "New password",
    resetBtn: "Reset password",
    resetDoneTitle: "Password updated!",
    resetDoneBody: "You can now sign in with your new password.",
    resetDoneCta: "Back to sign in",
    errCodeWrong: "That code does not match. Check it above and try again.",
    errNewPasswordShort: "Password must be at least 8 characters.",
    errEmailSend: "Could not send the email right now. Try again shortly.",
    backToSignIn: "Back to sign in",
    documentTitleSignIn: "Sign in — SouqAkar",
    documentTitleRegister: "Create an account — SouqAkar",
    pw: {
      show: "Show password",
      hide: "Hide password",
      reqLength: "At least 8 characters",
      reqMix: "Letters and numbers together",
      reqLong: "12 characters or more",
      required: "Required",
      optional: "Optional",
      met: "done",
      unmet: "not yet",
      strength: "Password strength",
      weak: "Weak",
      fair: "Fair",
      strong: "Strong",
      guessable: "easy to guess",
    },
  },
  register: {
    chooseTitle: "How will you use SouqAkar?",
    chooseSub: "This sets which fields and verification you need.",
    individualTitle: "Individual",
    individualBody: "Search, save and contact agents about properties.",
    agentTitle: "Agent / office",
    agentBody: "List properties and run your business on SouqAkar.",
    continueBtn: "Continue",
    back: "Back",
    personalInfo: "Personal details",
    businessInfo: "Business details",
    fullName: "Full name", fullNamePh: "e.g. Karim Hassan", fullNameErr: "Full name is required",
    mobile: "Mobile number", mobilePh: "e.g. 01001234567", mobileErr: "Enter a valid mobile number",
    email: "Email", emailPh: "you@example.com", emailErr: "Enter a valid email address",
    password: "Password", passwordErr: "Password must be at least 8 characters",
    businessName: "Business / office name", businessNamePh: "e.g. Hassan Real Estate", businessNameErr: "Business name is required",
    crn: "Commercial registration number", crnPh: "e.g. 123456", crnErr: "Commercial registration number is required",
    vat: "Tax number", vatPh: "e.g. 123-456-789", vatErr: "Tax number is required",
    submitIndividual: "Create my account",
    submitAgent: "Submit for verification",
    doneIndividualTitle: "Welcome to SouqAkar!",
    doneIndividualBody: "Your free account is ready. You can subscribe any time to unlock contact details, messaging and the AI assistant.",
    doneIndividualCta: "Go to my account",
    doneAgentTitle: "You're almost done!",
    doneAgentBody: "We're reviewing your commercial registration and tax documents — this usually takes one to two working days. We'll email you as soon as your account is approved.",
    doneAgentCta: "Back to home",
    errExists: "An account already exists for that email or number. Sign in instead.",
    errGeneric: "Could not create the account right now. Try again shortly.",
    haveAccount: "Already have an account?",
  },
  panel: {
    myAccount: "My account",
    memberFallback: "Member",
    memberOfPlan: "{plan} member",
    ownerPreviewPending: "Your listing is in review — this is how visitors will see it once approved.",
    ownerPreviewRejected: "This listing was rejected and is not visible to visitors. Edit it and submit again.",
    ownerPreviewPaused: "This listing is paused and is not visible to visitors. Resume it to show it again.",
    ownerPreviewGeneric: "This listing is not published; you are seeing it as its owner.",
    rejectionReason: "Rejection reason",
    pauseListing: "Pause",
    resumeListing: "Resume",
    statusChanged: "Listing status updated ✓",
    statusFailed: "The listing status could not be updated.",
    viewListing: "View listing",
    errCountRange: "Enter a whole number between {min} and {max}.",
    errWholeNumber: "Enter a whole number, with no fraction.",
    errPositive: "Enter a number greater than zero.",
    errFixFields: "Check the fields marked in red.",
    changePhoto: "Change photo",
    noPhotoChosen: "No photo chosen yet",
    removePhoto: "Remove photo",
    addMorePhotos: "Add more photos",
    photoCount: "{n} of {max}",
    photoCapHit: "{n} photo(s) skipped: the limit was reached.",
    coverHint: "The first photo is the cover. Drag to reorder.",
    cover: "Cover",
    moveEarlier: "Move earlier",
    moveLater: "Move later",
    videoLabel: "Video link (optional)",
    videoHint: "YouTube only — for example youtube.com/watch?v=… or youtu.be/…",
    videoInvalid: "That is not a valid YouTube link.",
    videoRemove: "Remove video",
    conversation: "Conversation",
    dismiss: "Dismiss",
    dismissTip: "Hidden on this device only for now. The broadcast itself is not deleted, and you can bring it back.",
    dismissed: "Dismissed",
    showDismissed: "Show dismissed",
    hideDismissed: "Hide them again",
    restoreOne: "Restore",
    restoreAll: "Restore all",
    noMessagesYet: "No messages in this conversation yet.",
    replyPlaceholder: "Write a reply…",
    send: "Send",
    replyFailed: "The reply could not be sent. Try again.",
    navDashboard: "My dashboard",
    welcome: "Welcome",
    welcomeNamed: "Welcome, {name}",
    unreadMessages: "Unread messages",
    aiThisMonth: "Consultations this month",
    currentPlan: "Your plan",
    unreadNote: "The unread count covers broadcasts newer than this device's last visit; the server keeps no read state for conversations.",
    browse: "Browse properties",
    navSaved: "Saved", navListings: "My listings", navMessages: "Messages",
    navAi: "AI assistant", navAccount: "Account",
    backToSite: "← Back to the site",
    logout: "Sign out",
    savedProperties: "Saved properties",
    removeFromSaved: "Remove from saved",
    noSaved: "No saved properties yet — browse listings and tap the heart to save one.",
    myListings: "My listings", addProperty: "Add a property", edit: "Edit",
    noMyListings: "You haven't listed a property yet.",
    upgradePlan: "Upgrade your plan",
    quotaUsed: "Used", quotaLimit: "Limit", quotaUnlimited: "Unlimited",
    limitReached: "You've reached your plan's listing limit. Upgrade to add more.",
    backToListings: "← Back to my listings",
    title: "Title", titlePlaceholder: "e.g. Two-bedroom apartment near the metro",
    description: "Description", descriptionPlaceholder: "Describe the property clearly: rooms, finishing, what's nearby…",
    type: "Type", listingType: "Listing type",
    location: "Location", city: "City", district: "District",
    pinHint: "Drag the marker to place the property on the map.",
    precisionLabel: "Location privacy:",
    exactLoc: "Show the exact location", approxLoc: "Approximate area only",
    areaSqm: "Area (m²)", priceLabel: "Price",
    bedrooms: "Bedrooms", bathrooms: "Bathrooms", kitchens: "Kitchens",
    furnished: "Furnished", yes: "Yes", no: "No",
    photo: "Property photo", choosePhoto: "Choose a photo", uploading: "Uploading…",
    photoRules: "JPEG, PNG or WebP, up to {mb} MB.",
    photoLimitsLoading: "Loading photo limits…",
    photoLimitsNotSetUp: "Photo limits are not set up for this market ({key}). Tell the admins.",
    photoLimitsUnavailable: "Could not load the photo limits. Try again.",
    errPhotoType: "Unsupported file type. Choose JPEG, PNG or WebP.",
    errPhotoSize: "That file is larger than {mb} MB.",
    errPhotoUpload: "Could not upload the photo. Try again.",
    publish: "Publish listing", publishing: "Publishing…",
    published: "Your listing has been submitted for review.",
    errRequired: "This field is required.",
    errTooLong: "At most {max} characters.",
    errCreate: "Could not publish the listing right now. Try again.",
    statusLabel: "Status",
    messages: "Messages", noThreads: "No messages yet.",
    broadcasts: "Platform announcements", noBroadcasts: "No announcements.",
    aiTitle: "My AI consultations", noSessions: "No past consultations.",
    aiPending: "Starting a new chat arrives with the AI assistant chapter.",
    aiQuota: "Consultations available this month",
    account: "Account", name: "Name", email: "Email", phone: "Mobile number",
    plan: "Plan", save: "Save changes", saving: "Saving…", saved: "Saved ✓",
    errSave: "Could not save. Try again.",
    renew: "Renew subscription", subscribe: "Subscribe",
    emailVerified: "✉✓ Email verified", emailNotVerified: "✉ Email not verified",
    documentTitle: "My account — SouqAkar",
  },
  pricing: {
    headline: "Choose your plan",
    loading: "Loading plans…",
    unavailable: "Plans could not be loaded right now.",
    perMonth: "/mo",
    free: "Free",
    mostPopular: "Most popular",
    currentPlan: "Current plan",
    choosePlan: "Choose plan",
    featListings: "listings",
    featListingsUnlimited: "Unlimited listings",
    featAi: "AI consultations per month",
    featAiUnlimited: "Unlimited AI consultations",
    featAiNone: "No AI consultations",
    featContact: "Contact listers",
    documentTitle: "Plans — SouqAkar",
    checkoutTitle: "Complete your subscription",
    planLabel: "Plan",
    priceLabel: "Amount",
    promoCodeLabel: "Promo code (optional)",
    promoCodePh: "Enter a code if you have one",
    confirmBtn: "Confirm subscription",
    working: "Working…",
    unlimited: "Unlimited",
    processing: "Processing…",
    successTitle: "Your plan is active",
    renewsAt: "Renews on",
    reference: "Reference",
    backToAccount: "← Go to my account",
    errCheckout: "Could not complete the subscription. Try again.",
    signInFirst: "Sign in to complete your subscription.",
    stepReview: "Review",
    stepPayment: "Payment",
    stepDone: "Done",
    currentPlanIs: "Your current plan",
    alreadyOnPlan: "You are already subscribed to this plan.",
    renewsOn: "Renews on",
    downgradeTitle: "You are about to move to a smaller plan",
    downgradeFrom: "You currently have",
    downgradeTo: "You would move to",
    downgradeLose: "What you would lose:",
    lossAi: "AI consultations per month",
    lossListings: "Listing allowance",
    lossContact: "Contact details",
    lossShown: "shown",
    lossHidden: "hidden",
    downgradeConfirm: "I understand, continue",
    downgradeCancel: "Keep my current plan",
    payTitle: "Payment method",
    payCard: "Card",
    payFawry: "Fawry",
    payVodafone: "Vodafone Cash",
    cardNumber: "Card number",
    cardName: "Name on card",
    cardExpiry: "Expiry",
    cardCvc: "CVC",
    cardInvalid: "Those card details are not valid.",
    cardDeclined: "Card declined. In test mode only the test cards are accepted.",
    vodafoneNumber: "Vodafone Cash number",
    vodafonePh: "01xxxxxxxxx",
    vodafoneInvalid: "That number is not valid.",
    fawryNote: "Pay this reference at any Fawry outlet within 24 hours.",
    testMode: "Test mode — no payment will be taken",
    testCardsHint: "Test cards: 4242 4242 4242 4242 or 5555 5555 5555 4444",
    payNow: "Confirm payment",
    continueToPay: "Continue to payment",
    zeroPriceSkip: "This plan costs you nothing right now, so there is no payment step.",
    mockNotice: "No payment was taken — the subscription is activated free of charge right now.",
  },
  chat: {
    title: "SouqAkar Assistant",
    subtitle: "An AI assistant — answers are estimates, not guarantees.",
    greeting: "Hi! I'm SouqAkar Assistant, an AI assistant. Ask me about buying versus renting, about prices, or paste a property's details for a quick read. All figures are estimates.",
    placeholder: "Type your question…",
    send: "Send",
    thinking: "Thinking…",
    quotaLeft: "Remaining this month",
    quotaSpent: "You've used this month's consultations. Upgrade for more.",
    signInFirst: "Sign in to use the assistant.",
    errNetwork: "Could not reach the assistant right now.",
    errBlocked: "The assistant isn't reachable from this domain yet — the server is being updated.",
    errGeneric: "Something went wrong. Try again.",
    endAndSave: "End and save this chat",
    saved: "Conversation saved ✓",
    saveFailed: "The conversation could not be saved.",
  },
  admin: {
    consoleTitle: "Admin console",
    signIn: {
      title: "Admin sign in",
      subtitle: "This console is for administrators. Sessions last 24 hours.",
      identifier: "Email or phone",
      password: "Password",
      submit: "Sign in",
      badCreds: "Those credentials are not correct.",
      failed: "Could not sign in. Try again.",
      rateLimited: "Too many attempts. Wait a moment and try again.",
      memberHint: "Looking for your own account? Sign in here.",
    },
    nav: {
      dashboard: "Dashboard",
      users: "Users",
      listings: "Listings",
      verification: "Agent verification",
      subscriptions: "Subscriptions",
      broadcast: "Broadcasts",
      markets: "Markets",
      reports: "Reports",
      payments: "Payments",
      settings: "Settings",
      admins: "Admins",
      signOut: "Sign out",
      backToSite: "Back to the site",
    },
    common: {
      search: "Search",
      refresh: "Refresh",
      save: "Save",
      saving: "Saving…",
      saved: "Saved ✓",
      cancel: "Cancel",
      edit: "Edit",
      loading: "Loading…",
      empty: "Nothing to show.",
      retry: "Try again",
      all: "All",
      unlimited: "Unlimited",
      none: "—",
      yes: "Yes",
      no: "No",
      actions: "Actions",
      count: "Count",
      required: "This field is required.",
    },
    errors: {
      sessionExpired: "The admin session has ended. Sign in again.",
      forbidden: "Your role does not permit this action.",
      needsRole: "This screen is available to the {role} role.",
      loadFailed: "Could not load the data.",
      saveFailed: "Could not save.",
    },
    health: {
      title: "System health",
      note: "Every check here is read-only: none writes anything, and none spends quota.",
      run: "Run again",
      running: "Checking…",
      lastRun: "Last run",
      build: "Build",
      buildUnknown: "unknown",
      branch: "Branch",
      buildNote: "Read from the commit at build time, so it cannot disagree with what was built.",
      probe: "Check",
      verdict: "Verdict",
      detail: "Detail",
      layer: "Layer",
      ms: "Time",
      ok: "OK",
      warn: "Warning",
      fail: "Failed",
      skipped: "Skipped",
      layerNetwork: "Network",
      layerCors: "CORS",
      layerProxy: "Proxy",
      layerUpstream: "Backend",
      layerAuth: "Session",
      layerClient: "Browser",
      pProxy: "Database proxy",
      pProxyWhat: "GET /countries?limit=1 — the cheapest possible read.",
      pPlans: "Public plans",
      pPlansWhat: "GET /plans — and whether contact_enabled is present.",
      pAuth: "Admin session",
      pAuthWhat: "One admin read, to confirm this console's token is still valid.",
      pClaude: "AI assistant proxy",
      pClaudeWhat: "A plain GET — no message sent, no quota spent.",
      pStorage: "Photo storage",
      pStorageWhat: "HEAD through the proxy — nothing is uploaded.",
      storagePending:
        "The photo-storage check is paused: it was measuring an old, suspended"
        + " project and reporting it healthy. It returns once worker v3.8.11 ships.",
      liveCall: "Send a real test message",
      liveCallWarn: "Spends one consultation from your monthly quota.",
      liveCallConfirm: "Send a real test message? One consultation will be used.",
      allOk: "Every check passed.",
      someFail: "A check failed — the detail below names the layer responsible.",
      corsHint: "A refused origin means the worker's allowlist changed, not that a service is down.",
    },
    dashboard: {
      title: "Dashboard",
      users: "Users",
      agents: "Agents",
      individuals: "Individuals",
      activeListings: "Active listings",
      pendingListings: "Listings awaiting review",
      rejectedListings: "Rejected listings",
      pendingVerifications: "Pending verifications",
      paidSubscribers: "Paid subscribers",
      countedNote: "Every number here is an actual count of database rows at load time, not an estimate.",
      noRevenue: "There is no revenue figure here: the platform does not record payments in the database yet, so any amount shown would be invented.",
    },
    users: {
      title: "Users",
      name: "Name",
      email: "Email",
      phone: "Phone",
      role: "Type",
      plan: "Plan",
      status: "Status",
      subscription: "Subscription",
      joined: "Joined",
      renews: "Renews",
      verified: "Verified",
      emailVerified: "Email verified",
      searchPlaceholder: "Search by name, email or phone",
      editTitle: "Edit user",
      noDelete: "Users cannot be deleted from this console.",
    },
    listings: {
      title: "Listing moderation",
      listing: "Listing",
      owner: "Owner",
      price: "Price",
      location: "Location",
      status: "Status",
      tag: "Tag",
      views: "Views",
      inquiries: "Inquiries",
      approve: "Approve",
      approveNeedsPhotos: "Cannot approve: this listing needs at least {n} photo(s).",
      reject: "Reject",
      pause: "Pause",
      reactivate: "Reactivate",
      flagReason: "Rejection reason",
      flagReasonPlaceholder: "Write a reason the lister will see",
      flagReasonRequired: "A rejection reason is required before rejecting.",
      returnToReview: "Return to review",
      moderated: "Listing status updated ✓",
      titlePolicy: "The title renders exactly as the lister typed it.",
    },
    verification: {
      title: "Agent verification requests",
      applicant: "Applicant",
      business: "Business name",
      crn: "Commercial registration",
      vat: "Tax card",
      experience: "Years of experience",
      documents: "Documents",
      nationalId: "National ID",
      license: "Licence",
      selfie: "Selfie",
      officeContacts: "Office contacts",
      approve: "Approve",
      reject: "Reject",
      rejectReason: "Rejection reason",
      rejectReasonRequired: "A rejection reason is required before rejecting.",
      updated: "Request updated ✓",
      docsNote: "The console shows only whether a document was uploaded; the files themselves are not exposed by the current API.",
    },
    plans: {
      title: "Plans",
      tabPlans: "Plans",
      tabPromos: "Promotions",
      newPlan: "+ New plan",
      newPromo: "+ New promotion",
      subscribers: "Subscribers",
      statusActive: "Active",
      statusArchived: "Archived",
      archive: "Archive",
      restore: "Restore",
      editPlan: "Edit plan",
      createPlan: "New plan",
      archiveConfirm: "{n} subscriber(s) on this plan. Archiving hides it from the pricing page and touches no current subscriber.",
      archiveConfirmZero: "No subscribers on this plan. Archiving hides it from the pricing page.",
      restoreConfirm: "Show this plan on the pricing page again?",
      newPlanEnabledNote: "A new plan arrives enabled from the server, so it appears on the pricing page as soon as it is created.",
      noDeleteLaw: "Plans are never deleted, only archived — historic subscriber integrity.",
      promoLive: "Live",
      promoScheduled: "Scheduled",
      promoExpired: "Expired",
      promoPaused: "Paused",
      promoWindow: "Window",
      promoUses: "Uses",
      promoTarget: "Plan",
      promoDiscount: "Discount",
      launchCampaign: "Launch campaign",
      launchGuard: "This is the running launch promotion. Edit it anyway?",
      pause: "Pause",
      activate: "Activate",
      editPromo: "Edit promotion",
      createPromo: "New promotion",
      deleteBlocked: "A promotion that has been used cannot be deleted; pause it instead.",
      endsIn: "ends {when}",
      extend7: "+7 days",
      allPaidPlans: "All paid plans",
      plansCount: "{n} plans",
      expandRows: "Show rows",
      collapseRows: "Hide rows",
      mixedState: "Mixed",
      codedMultiBlocked: "One code cannot span plans: the database enforces code uniqueness. Make the campaign automatic (no code), or create a separate promotion per plan with its own code.",
      perPlanResult: "Per-plan result",
      rowFailed: "failed",
      rowOk: "done",
      retryFailed: "Retry the failed ones",
      creatingRows: "Creating: {done} of {total}",
      extend30: "+30 days",
      extendTitle: "Extend the promotion's end date",
      extendedTo: "Extended to {date}",
      usedEditWarn: "This promotion has been used {n} time(s). Everyone who redeemed it keeps their terms; the change applies to future checkouts only. Continue?",
      usedNoDelete: "Delete is not offered for a used promotion: redemption records reference it. Pause it instead.",
      name: "Name",
      price: "Price",
      maxProperties: "Listing limit",
      listingDuration: "Listing duration (days)",
      listingDurationHelp: "Empty on the free plan: its listings take the market's «free listing duration» setting.",
      refreshesPerMonth: "Refreshes per month",
      refreshesHelp: "0 = none, empty = unlimited.",
      aiLimit: "AI consultation limit",
      enabled: "Enabled",
      contactEnabled: "Show contact details",
      contactHelp: "When off, the lister's contact details are hidden from members on this plan.",
      unlimitedHelp: "Leave empty to mean unlimited.",
    },
    promotions: {
      title: "Promotions",
      name: "Promotion name",
      plan: "Plan",
      discountType: "Discount type",
      percent: "Percentage",
      fixed: "Fixed amount",
      discountValue: "Discount value",
      startsAt: "Starts",
      endsAt: "Ends",
      active: "Active",
      durationDays: "Subscription length (days)",
      message: "Message sent to the subscriber",
      code: "Code",
      codeHelp: "Leave empty to make the promotion automatic for everyone.",
      maxUses: "Maximum uses",
      usesCount: "Times used",
      oncePerUser: "Once per user",
      create: "Create promotion",
      remove: "Delete",
      confirmRemove: "Delete this promotion permanently?",
      created: "Promotion created ✓",
      removed: "Promotion deleted ✓",
      automatic: "Automatic",
    },
    broadcast: {
      title: "Broadcasts",
      subject: "Subject",
      message: "Message",
      target: "Recipient",
      everyone: "All members",
      oneUser: "A specific member",
      pickUser: "Choose the member",
      send: "Send",
      sent: "Sent ✓",
      history: "Previous broadcasts",
      recipients: "Recipients",
      date: "Date",
      toAll: "Everyone",
      sendNote: "The message lands in the in-platform inbox only; no email is sent.",
    },
    admins: {
      title: "Admins",
      superOnly: "Managing admin accounts is available to the Super Admin only.",
      name: "Name",
      email: "Email",
      phone: "Phone",
      role: "Role",
      status: "Status",
      newAdmin: "New admin",
      password: "Password",
      passwordHelp: "At least 8 characters.",
      roleAdmin: "Admin",
      roleSupervisor: "Supervisor",
      roleSuper: "Super Admin",
      noSuperCreate: "A Super Admin cannot be created from this console.",
      remove: "Delete",
      confirmRemove: "Delete this admin account?",
      created: "Account created ✓",
      removed: "Account deleted ✓",
    },
    markets: {
      title: "Markets",
      countries: "Countries",
      cities: "Cities",
      districts: "Districts",
      nameAr: "Arabic name",
      nameEn: "English name",
      isoCode: "ISO code",
      currency: "Currency",
      phoneCode: "Phone code",
      activeState: "State",
      active: "Active",
      inactive: "Disabled",
      readOnly: "Countries and cities are read-only: the server has no route for editing them yet. A district's short name is the one field editable here.",
      inCity: "in",
      shortName: "Short name",
      shortNameAr: "Arabic",
      shortNameEn: "English",
      shortNameHelp: "Shown on the home area tile. Leave empty to show the full name.",
      shortNameTooLong: "40 characters at most.",
    },
    reports: {
      title: "Reports",
      listingsByStatus: "Listings by status",
      listingsByCity: "Listings by city",
      listingsByType: "Listings by type",
      usersByPlan: "Users by plan",
      promoUsage: "Promotion usage",
      derivedNote: "Everything on this page is computed from real rows at load time.",
      noRevenue: "There is no revenue report: the database keeps no payments table, and no correct amount can be derived from what is available.",
    },
    payments: {
      title: "Payments",
      noSource: "The platform records no payment in the database, so there is nothing to show here.",
      legacyNote: "The old console displayed six payments with fixed names, amounts and dates written into the page itself — not real data. They were removed rather than reproduced.",
      needed: "Enabling this screen needs a payments table and a route to read it.",
    },
    settings: {
      title: "Settings",
      account: "Your account",
      signedInAs: "Signed in as",
      role: "Role",
      changePassword: "Change password",
      newPassword: "New password",
      tooShort: "The password must be at least 8 characters.",
      changed: "Password changed ✓",
      platformToggles: "Platform settings",
      togglesNote: "There are no platform settings to change here. The old console's switches (signups, AI, maintenance mode) were saved in the admin's own browser and never affected the platform at all; they were not rebuilt, because a switch that does nothing is worse than no switch.",
      reloadNote: "After a page reload the session stays valid but the account name is not shown until you sign in again.",
    },
  },
  notFound: {
    title: "That page does not exist",
    body: "The link you opened does not lead to a page on SouqAkar. It may be out of date or mistyped.",
    home: "Go to the homepage",
    search: "Browse properties",
    pricing: "Plans and pricing",
  },
  agent: {
    documentTitle: "Agent panel — SouqAkar",
    title: "Agent panel",
    navDashboard: "Performance",
    navListings: "My properties",
    navMessages: "Messages",
    navAi: "AI advisor",
    navAccount: "Account",
    tierGold: "Gold agent",
    tierPlatinum: "Platinum agent",
    verified: "Verified agent",
    notVerified: "Verification not complete",
    verificationPending: "Your verification request is under review.",
    verificationRejected: "Your verification request was rejected.",
    totalListings: "Total properties",
    activeListings: "Active properties",
    totalViews: "Total views",
    totalInquiries: "Total inquiries",
    perListing: "Performance per property",
    listing: "Property",
    views: "Views",
    inquiries: "Inquiries",
    noListings: "You have not added a property yet.",
    countedNote: "These figures come from the view and inquiry counters recorded on each property.",
    noLeadRecords: "There is no itemised lead record: the platform counts inquiries but keeps no per-inquiry detail.",
    needPlan: "The agent panel is available on the paid agent plans.",
    seePlans: "See plans",
  },
  status: { fixtureNotice: "Showing local sample data." },
};

export const dictionaries: Record<Locale, Strings> = { ar, en };
